import { useMemo } from "react";

interface PricePerUnitProps {
  amountUnit?: string;
  priceNoFormat?: number;
  ppu?: string;
  currencySymbol?: string;
}

const ProductPricePerUnit = ({
  amountUnit,
  priceNoFormat,
  ppu,
  currencySymbol = "€",
}: PricePerUnitProps): JSX.Element | null => {
  const lowercasedPpu = ppu?.toLowerCase();
  const lowercasedUnit = amountUnit?.toLowerCase() ?? "";

  const hasData = (ppu && ppu.length > 0) || (amountUnit && priceNoFormat !== undefined);
  if (!hasData) return null;

  const computedPPU = useMemo(() => {
    if (ppu && ppu.length > 0) {
      return lowercasedPpu as string;
    }
    if (!amountUnit || priceNoFormat === undefined) {
      return "";
    }

    let qty: number;
    let unit: string;

    const multiMatch = amountUnit.match(/^([\d.,]+)x([\d.,]+)\s*([^\d\s]+)/u);
    if (multiMatch) {
      const count = parseFloat(multiMatch[1].replace(",", "."));
      const perPack = parseFloat(multiMatch[2].replace(",", "."));
      qty = count * perPack;
      unit = multiMatch[3].toLowerCase();
    } else {
      const singleMatch = amountUnit.match(/([\d.,]+)\s*([^\d\s]+)/u);
      if (!singleMatch) {
        return "";
      }
      qty = parseFloat(singleMatch[1].replace(",", "."));
      unit = singleMatch[2].toLowerCase();
    }

    if (qty <= 0) {
      return "";
    }

    const rawPPU = priceNoFormat / qty;
    const ppuFixed = rawPPU.toFixed(2);
    const ppuFormatted = ppuFixed.replace(".", ",");
    return `${ppuFormatted} ${currencySymbol} / 1 ${unit}`;
  }, [ppu, amountUnit, priceNoFormat, currencySymbol, lowercasedPpu]);

  return (
    <div className="product-card__amount-ppu" role="group" aria-label={computedPPU}>
      <div className="product-card__unit-details" aria-hidden="true">
        {lowercasedUnit && <div className="product-card__unit-amount">{lowercasedUnit}</div>}
        <span className="product-card__unit-divider">|</span>
        <div className="product-card__unit-price">{computedPPU}</div>
      </div>
    </div>
  );
};

export default ProductPricePerUnit;